import { memo } from 'react';
import { 
  Wind, 
  UtensilsCrossed, 
  Droplets, 
  Zap, 
  Package, 
  ShieldAlert, 
  Wrench, 
  Calendar 
} from 'lucide-react';
import { Appliance, ApplianceCategory, RiskScore } from '@/types';

const categoryIcons: Record<ApplianceCategory, React.FC<{ className?: string }>> = {
  kitchen: UtensilsCrossed,
  laundry: Wind,
  hvac: Wind,
  plumbing: Droplets,
  electrical: Zap,
  other: Package,
};

const ApplianceCard = memo(({ appliance, riskScore, recallCount, onClick }: {
  appliance: Appliance;
  riskScore: RiskScore | null;
  recallCount: number;
  onClick: () => void;
}) => {
  const CategoryIcon = categoryIcons[appliance.category || 'other'] || Package;

  const warrantyStatus = () => {
    if (!appliance.warrantyExpiresAt) return 'Unknown';
    const warrantyDate = new Date(appliance.warrantyExpiresAt);
    if (warrantyDate < new Date()) return 'Expired';
    return 'Active';
  };

  const warrantyStyle = () => {
    const status = warrantyStatus();
    if (status === 'Expired') return 'text-red-600 dark:text-red-400';
    if (status === 'Active') return 'text-emerald-600 dark:text-emerald-400';
    return 'text-slate-500 dark:text-slate-400';
  };

  const costThresholdWarning = () => {
    if (
      appliance.totalServiceCost === undefined || 
      appliance.purchasePrice === undefined || 
      appliance.purchasePrice <= 0
    ) return null;

    if (appliance.totalServiceCost > appliance.purchasePrice * 0.7) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          <ShieldAlert className="w-3 h-3 mr-1" />
          ⚠ Consider replacing
        </span>
      );
    }
    return null;
  };

  return (
    <div 
      onClick={onClick}
      className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all active:scale-[0.98]"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300">
            <CategoryIcon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              {appliance.name || `${appliance.make} ${appliance.model}`}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {appliance.make} {appliance.model}
              {appliance.modelNumber && <span className="ml-1 text-slate-400">({appliance.modelNumber})</span>}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-end space-y-2">
          {riskScore && (
            <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
              riskScore === 'high' 
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                : riskScore === 'medium' 
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' 
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
            }`}>
              {riskScore.charAt(0).toUpperCase() + riskScore.slice(1)} Risk
            </span>
          )}
          
          {recallCount > 0 && (
            <div className="flex items-center px-2 py-1 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
              <ShieldAlert className="w-3 h-3 mr-1" />
              <span className="text-xs font-semibold">{recallCount} Recall{recallCount > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>

      {costThresholdWarning() && (
        <div className="mb-3">
          {costThresholdWarning()}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-700">
        <div className="flex flex-col">
          <span className="text-xs text-slate-500 dark:text-slate-400">Total Service Cost</span>
          <span className="text-sm font-medium text-slate-900 dark:text-white">
            ${appliance.totalServiceCost?.toLocaleString() || '0'}
          </span>
        </div>

        <div className="flex flex-col items-end">
          <span className="text-xs text-slate-500 dark:text-slate-400">Warranty Status</span>
          <div className="flex items-center mt-1">
            <Calendar className="w-3 h-3 mr-1.5 text-slate-400" />
            <span className={`text-sm font-medium ${warrantyStyle()}`}>
              {warrantyStatus()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ApplianceCard;
